import { ref, computed } from 'vue';
import { useSupabaseClient } from '~/plugins/supabase';
import { useUserStore } from '~/stores/user';

/*
 * These three are at module scope, and exported, on purpose.
 *
 * The navbar shows an initials <div> until the generated image has uploaded.
 * When that fallback used its own initials rule and no background colour, the
 * placeholder and the final image looked like two different avatars and the
 * swap was a visible pop. Anything rendering a fallback must use these, so the
 * two paths cannot drift apart.
 *
 * They are pure — no supabase, no store — which is why they don't need to live
 * inside the composable.
 */
export const getInitials = (name) => {
  if (!name) return '';

  if (name == '[Deleted User]') {
    return 'DU'; // Special case for deleted user
  }
  const words = name.match(/\S+/g) || [];

  if (words.length > 1) {
    return words
      .map((n) => n[0])
      .join('')
      .slice(0, 2); // First letters of multiple words
  }

  if (name[0] === '@') {
    return name.slice(1, 3); // If name starts with @, skip it and take next two characters
  }

  const hyphenIndex = name.indexOf('-');
  if (hyphenIndex !== -1 && hyphenIndex + 1 < name.length) {
    return name[0] + name[hyphenIndex + 1]; // First letter + first character after hyphen
  }

  return name.slice(0, 2); // Default case: take first two characters
};

export const getConsistentColor = (userId) => {
  const colors = [
    '#4A4E69', // Slate gray
    '#6C757D', // Muted charcoal
    '#ADB5BD', // Soft gray
    '#495057', // Gunmetal
    '#B0BEC5', // Misty gray-blue
    '#2F3E46', // Deep forest green-gray
    '#6D6875', // Desaturated mauve-gray
    '#5C677D', // Cool steel blue
    '#3D405B', // Indigo-gray
    '#444B54', // Charcoal navy
    '#708090', // Slate gray-blue
  ];

  if (!userId) return colors[0];

  // Create consistent index based on user ID
  const hash = userId.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);

  const colorIndex = hash % colors.length;
  return colors[colorIndex];
};

export const getTextColor = (hexColor) => {
  // Calculate brightness from hex
  const c = hexColor.substring(1); // remove #
  const rgb = parseInt(c, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;

  // Perceived brightness formula
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

  return brightness > 160 ? '#000000' : '#FFFFFF';
};

/**
 * The display name the initials are derived from. Exported alongside the
 * helpers so a fallback renderer picks the same name the canvas would have.
 */
export const getAvatarDisplayName = (profileData) => {
  if (!profileData) return '';
  // Parenthesised on purpose: `'@' + username || fallback` binds as
  // `('@' + username) || fallback`, which is never falsy, so the fallback was
  // dead and a missing username rendered '@undefined' — initials "UN".
  return profileData.show_name
    ? profileData.name || 'Anon User'
    : profileData.username
      ? '@' + profileData.username
      : 'Anon User';
};

/**
 * Merge a freshly fetched profile row over the one in the store WITHOUT
 * letting a null avatar_url blank an avatar that is already showing.
 *
 * `select('*')` returns avatar_url as an explicit null while generation is
 * still in flight, and object spread copies nulls like any other value — so a
 * plain `{ ...current, ...fetched }` erases it. Only a real string may replace
 * a real string; a genuine removal arrives through the store, not through a
 * race with bootstrap.
 */
export const mergeProfile = (current, fetched) => {
  if (!fetched) return current;
  const merged = { ...(current || {}), ...fetched };
  if (!fetched.avatar_url && current?.avatar_url) {
    merged.avatar_url = current.avatar_url;
  }
  return merged;
};

export const useAvatar = () => {
  const supabase = useSupabaseClient();
  const userStore = useUserStore();

  const avatarUrl = ref('');
  const isGenerating = ref(false);

  /**
   * Generate an avatar for a user, either from their existing avatar_url or by creating an initial-based avatar
   * @param {Object} profileData - The user profile data
   * @param {Boolean} forceInitials - Force generation of initials avatar even if avatar_url exists
   * @returns {Promise<String>} - URL of the avatar
   */
  const generateAvatar = async (
    profileData,
    profileUpdated = false,
    forceInitials = false
  ) => {
    try {
      if (!profileData) {
        console.warn('No profile data provided to generateAvatar');
        return null;
      }

      isGenerating.value = true;

      // Use existing avatar if available and not forcing initials
      if (profileData.avatar_url && !forceInitials && !profileUpdated) {
        avatarUrl.value = profileData.avatar_url;
        return avatarUrl.value;
      }

      // Generate display name for initials
      const displayName = getAvatarDisplayName(profileData);

      // Get initials from name
      const initials = profileData.is_deleted
        ? 'DU'
        : getInitials(displayName.toUpperCase());

      // Generate consistent color based on user ID
      const backgroundColor = profileData.is_deleted
        ? '#FF4444'
        : getConsistentColor(profileData.id);
      const textColor = profileData.is_deleted
        ? '#FFFFFF'
        : getTextColor(backgroundColor);

      // Create canvas with initials
      const { canvas, blob } = await createInitialsCanvas(
        initials,
        backgroundColor,
        textColor,
        profileData.is_deleted
      );

      // If it's the current user, upload to storage and update profile
      const isCurrentUser = userStore.user?.id === profileData.id;

      if (isCurrentUser) {
        const newAvatarUrl = await uploadAndUpdateAvatar(profileData.id, blob);
        return newAvatarUrl;
      } else {
        // For other users, just return the data URL
        avatarUrl.value = canvas.toDataURL('image/jpeg');
        return avatarUrl.value;
      }
    } catch (error) {
      console.error('Error generating avatar:', error);
      return null;
    } finally {
      isGenerating.value = false;
    }
  };

  /**
   * Delete the current avatar and replace with initials
   * @param {Object} profileData - The user profile data
   * @returns {Promise<String>} - URL of the new avatar
   */
  const deleteAvatar = async (profileData) => {
    try {
      if (!profileData || !userStore.user?.id) {
        throw new Error('No profile data or user is not authenticated');
      }

      const userId = profileData.id;
      const fileName = `${userId}.jpg`;
      const filePath = `Profile Pictures/${fileName}`;
      const bucketName = 'Profile Info';

      // Delete the file from storage bucket
      const { error: deleteError } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (deleteError) {
        console.warn('Error removing avatar file:', deleteError);
        // Continue anyway - the file might not exist
      }

      // Generate new initials avatar
      return await generateAvatar(profileData, true);
    } catch (error) {
      console.error('Error deleting avatar:', error);
      throw error;
    }
  };

  const createInitialsCanvas = (
    initials,
    backgroundColor,
    textColor,
    deleted
  ) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');

      // Background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Text
      ctx.font = deleted ? 'bold italic 90px Courier New' : 'bold 80px Arial';

      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      deleted
        ? ctx.fillText(initials, canvas.width / 2, canvas.height / 1.8)
        : ctx.fillText(initials, canvas.width / 1.95, canvas.height / 1.8);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          resolve({ canvas, blob });
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const uploadAndUpdateAvatar = async (userId, blob) => {
    const fileName = `${userId}.jpg`;
    const filePath = `Profile Pictures/${fileName}`;
    const bucketName = 'Profile Info';

    // Upload blob to storage
    const { error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(filePath, blob, {
        upsert: true,
        contentType: 'image/jpeg',
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(bucketName).getPublicUrl(filePath);

    // Add cache busting parameter
    const newAvatarUrl = `${publicUrl}?t=${Date.now()}`;

    // Update profile in database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: newAvatarUrl })
      .eq('id', userId);

    if (updateError) throw updateError;

    // Update local state
    avatarUrl.value = newAvatarUrl;

    // Update user store
    if (userStore.user) {
      userStore.user.avatar_url = newAvatarUrl;
    }
    if (userStore.userProfile) {
      userStore.userProfile.avatar_url = newAvatarUrl;
    }

    return newAvatarUrl;
  };

  return {
    generateAvatar,
    deleteAvatar,
    avatarUrl: computed(() => avatarUrl.value),
    isGenerating: computed(() => isGenerating.value),
    // Re-exported so callers that already have the composable don't need a
    // second import just to render a matching fallback.
    getInitials,
    getConsistentColor,
    getTextColor,
    getAvatarDisplayName,
  };
};
